import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import { formatDate, getInitialShipDate } from "@dana/Common/utils";
import ProductAvailabilityDetails from "@dana/Components/CheckAvailability/ProductAvailabilityDetails";
import { DanaWarehouseDto, InventryLocation, OrderTypes } from "@dana/Handlers/ProductAvailabilityHandler";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@dana/MobiusOverrides/DatePicker/DatePicker";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { createFromProduct, ProductInfo } from "@dana/Overrides/Common/ProductInfo";
import ProductAvailability, { ProductAvailabilityStyles } from "@dana/Overrides/Components/ProductAvailability";
import removeCartLine from "@dana/Overrides/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@dana/Overrides/Store/Pages/Cart/Handlers/UpdateCartLine";
import CartLineQuantity, { CartLineQuantityStyles } from "@dana/Overrides/Widgets/Cart/CartLineQuantity";
import { getProduct, getProductAvailabilityResponse } from "@dana/Services/DanaProductAvailabilityService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import ProductAvailabilityState from "@dana/Store/Data/ProductAvailability/ProductAvailabilityState";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    ProductModel,
    ProductSettingsModel,
    PromotionModel,
    WarehouseModel
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Trash2 from "@insite/mobius/Icons/Trash2";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import React, { ChangeEvent, FC, ReactNode, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
    showVmiBinDetails?: boolean;
    refreshCart?: (type: string) => void;
    warehouses?: WarehouseModel[]
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const session = getSession(state);
    const billtoCustomer = getBillToState(state, state.context.session.billToId);
    const locations = state.selectedInventryLocationsState;
    return {
        session,
        billtoCustomer,
        locations,
        isEmea: useIsEmea(state),
        isNARegion: useIsNA(state)
    };
};

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productDetailLink?: LinkPresentationProps;
    productErpNumberText?: TypographyProps;
    countryOfOrigin?: TypographyPresentationProps;
    customerNumber?: TypographyPresentationProps;
    badgeRed?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    cartExtendedPrice?: SmallHeadingAndTextStyles;
    estShipDate?: InjectableCss;
    shipDateModal?: InjectableCss;
    shipToLocationGridItem?: GridItemProps;
    shipToLocationCenter?: TypographyPresentationProps;
    shipToLocationName?: TypographyPresentationProps;
    shipToLocationLink?: LinkPresentationProps;
    selecteProductModalTitle?: InjectableCss;
    selectedProductErpNo?: TypographyPresentationProps;
    selecteProductTitle?: TypographyPresentationProps;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    productBinNumberGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    headingText?: SmallHeadingAndTextStyles;
    requestedShipDateTextField?: TextFieldPresentationProps;
    requestedShipDate?: TypographyPresentationProps;
    selectedProductModal?: ModalPresentationProps;
    checkAvailabilityButton?: ButtonPresentationProps;
    requestedDatePickerGridItem?: GridItemProps;
    requestedDatePicker?: DatePickerPresentationProps;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: [10, 10, 10, 3, 3],
        css: css``,
    },
    cartLineInfoContainer: { gap: 16 },
    cartLineInfoGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            @media only screen and (max-width: 992px) {
                order: 3;
            }
        `,
    },
    productBrandAndDescriptionGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    customerNumber: {
        css: css`
            width: 100%;
            word-wrap: break-word;
            display: block;
        `,
    },
    productPriceAndQuantityGridItem: { width: [12, 12, 12, 9, 9] },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    quantityGridItem: { width: [12, 12, 12, 2, 2] },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
            align-items: center;
            @media only screen and (max-width: 992px) {
                order: 2;
            }
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productBinNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    requestedShipDate: {
        css: css`
            font-size: 10px;
            text-transform: uppercase;
            font-weight: 600;
        `,
        mergeCss: false,
    },
    selectedProductModal: {
        size: 800,
    },
    shipToLocationGridItem: {
        css: css`
        
        
        `
    }
};

const CartLineCardCondensed: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    removeCartLine,
    updateCartLine,
    extendedStyles,
    showRemoveAction,
    showVmiBinDetails,
    session,
    billtoCustomer,
    refreshCart,
    warehouses,
    locations,
    isEmea,
    isNARegion,
}) => {
    const toasterContext = useContext(ToasterContext);
    const region = session.language?.properties?.region;
    const currentMode = getCookie(NavigationModeCookieName);
    const [requestedShipDateValue, setRequestedShipDate] = useState(cartLine?.properties?.requestedShipDate);
    const [openShipDateModal, SetOpenShipDateModal] = useState(false);
    const [openAvailabilityModal, SetOpenAvailabilityModal] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean | undefined>(false);
    const [qty, setQty] = useState(cartLine?.qtyOrdered?.toString() ?? "1");
    const [productAvilabilityModel, setProductAvilabilityModel] = useState<ProductAvailabilityState>();
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [selectedProductModel, setSelectedProduct] = useState<ProductModel>();
    const [productInfo, setProductInfo] = useState<ProductInfo>();
    const [availabilityModalIsOpen, setAvailabilityModalIsOpen] = useState(false);
    const [location, setLocation] = useState<DanaWarehouseDto | undefined>(undefined);
    const isEmergencyOrder =
        (cart?.properties?.isEmergencyOrder !== undefined &&
            cart.properties.isEmergencyOrder.toLocaleLowerCase() === "true") ??
        false;
    const isAnnualReturnsOrder =
        (cart?.properties?.isAnnualReturnsOrder !== undefined &&
            cart.properties.isAnnualReturnsOrder?.toLocaleLowerCase() === "true") ??
        false;
    const [productContext, setProductContext] = useState<ProductContextModel>({
        product: selectedProductModel || ({} as any),
        productInfo: productInfo || ({} as any),

    });
    const orderTypeCookie = getCookie('modalOrderType');
    const [orderType, setOrderType] = useState<string>(orderTypeCookie || OrderTypes.STOCK);

    const currentWarehouse = warehouses?.find((p) => p.properties?.plantCode && cartLine?.properties?.location && p.properties.plantCode === cartLine.properties.location);

    const qtyOrderedChangeHandler = (qtyOrdered: number, location: InventryLocation | undefined) => {

        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            let shipDate: Date | undefined;
            if (location?.committedDate) {
                shipDate = new Date(location.committedDate);
            }
            let selectedQuantity = locations?.selectedLocations?.find((p) => p.id === location?.id)?.selectedQuantity ?? qtyOrdered;
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: shipDate === undefined ? {} : { "estimatedShipDate": shipDate.toLocaleDateString(), "location": location?.location?.properties?.plantCode ?? '' },
                    qtyOrdered: selectedQuantity,
                },
                isEmergencyOrder,
                isAnnualReturnsOrder,
                onSuccess(line?: CartLineModel) {
                    if (line && line.isQtyAdjusted) {
                        const message = `${translate(
                            "The requeted product has been added your cart.",
                        )}\n${translate(
                            "The quantity added to your cart was changed due to rounding or minimum/maximum quantity rules.",
                        )}`;
                        toasterContext.addToast({
                            body: message,
                            messageType: "info",
                            timeoutLength: 5000,
                            isFocusAfterMount: true,
                        });
                    }
                },
            });
            if (isEmergencyOrder && refreshCart) {
                refreshCart(OrderTypes.EMERGENCY);
            } else if (isAnnualReturnsOrder && refreshCart) {
                refreshCart(OrderTypes.ANNUALRETURNS);
            }
        }
    };

    const parseShipDate = (dateStr: string | undefined): Date | undefined => {
        if (!dateStr) return undefined;
        const parts = dateStr.split("/");
        if (parts.length !== 3) return new Date(dateStr) || undefined;
        const month = isEmea ? Number(parts[1]) : Number(parts[0]);
        const day = isEmea ? Number(parts[0]) : Number(parts[1]);
        const year = Number(parts[2]);
        if (!month || !day || !year || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
        return new Date(year, month - 1, day);
    };

    const requestedShipDateChanged = (selectedDay: DatePickerState) => {
        let value = selectedDay.selectedDay?.toDateString() ?? new Date().toDateString();

        if (isEmea) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const selected = new Date(selectedDay.selectedDay ?? new Date());
            selected.setHours(0, 0, 0, 0);

            const dayOfWeek = selected.getDay(); // 0 = Sunday, 6 = Saturday

            if (dayOfWeek === 0 || dayOfWeek === 6 || selected < today) {
                value = "";
            }
        }
        if (value) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: { requestedShipDate: value },
                },
            });
            setRequestedShipDate(value);
        }
    };

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setRequestedShipDate(event.currentTarget.value);
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id, isEmergencyOrder, isAnnualReturnsOrder });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);
    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    const handleOpenShipDateModal = () => {
        SetOpenShipDateModal(true);
    };

    const handleCloseShipDateModal = () => {
        SetOpenShipDateModal(false);
    };

    const handleOpenAvailabilityModal = () => {
        SetOpenAvailabilityModal(true);
        SetOpenShipDateModal(false);
        editQuantity(cartLine.qtyOrdered?.toString() ?? "1");
    };

    const handleCloseAvailabilityModal = () => {
        SetOpenAvailabilityModal(false);
    };

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));

    useEffect(() => { }, [requestedShipDateValue, qty]);

    const setOrderTypeHandler = (value: string) => {
        setOrderType(value);
        setCookie("modalOrderType", value);
    }

    const debouncedEditQuantity = debounce((value: string) => {
        setQty(value.toString());
        editQuantity(value);
    }, 500);

    const setQuantityHandler = (value: number) => {
        debouncedEditQuantity(value?.toString());
    };

    const editQuantity = async (quantity: string) => {
        setIsLoading(true);
        let quantityOrdered = parseInt(quantity ?? 1, 10)
        const response = await getProductAvailabilityResponse({
            productNumber: cartLine.erpNumber,
            plantCode: session?.properties?.plantCode,
            quantity: quantityOrdered,
            orderType: isEmergencyOrder
                ? OrderTypes.EMERGENCY
                : isAnnualReturnsOrder
                    ? OrderTypes.ANNUALRETURNS
                    : OrderTypes.STOCK,
            customerNumber: billtoCustomer?.value?.customerNumber ?? "null",
        });

        const result = await getProduct(cartLine.productId ?? "");
        if ("error" in result) {
            setIsLoading(false);
            setErrorMessage(result.errorMessage);
        } else {
            let prodInfo = createFromProduct(result, quantityOrdered);
            prodInfo = {
                ...prodInfo,
                qtyOrdered: quantityOrdered,
            };

            setProductContext({
                product: result,
                productInfo: prodInfo,
                onQtyOrderedChanged: setQuantityHandler,
            });

            setProductAvilabilityModel({
                productCustomerPrice: response.customerPartPrice,
                productAvailability: response.inventory,
                locations: response.locations ?? [],
                productNumber: response.inventory?.partNumber || "",
                plantCode: response.inventory?.plantCode || "",
                orderType: isEmergencyOrder
                    ? OrderTypes.EMERGENCY
                    : isAnnualReturnsOrder
                        ? OrderTypes.ANNUALRETURNS
                        : OrderTypes.STOCK,
                avilabilityLoadSuccess: true,
                errorMessage: "",
            });
            const lineLocation = getCookie('lineLocation');
            if (lineLocation) {
                const currentLocation = response?.locations?.find((p) => p.location?.properties?.plantCode === lineLocation);
                if (currentLocation) {
                    setLocation(currentLocation.location);
                }

            }
            setIsLoading(false);
        }
    };

    const buttonClickHandler = () => {
        setAvailabilityModalIsOpen(true);
        editQuantity(cartLine.qtyOrdered?.toString() ?? "1");
    };

    const modalCloseHandler = () => {
        setAvailabilityModalIsOpen(false);
    };

    const selecteProductModalTitle = (
        <StyledWrapper {...styles.selecteProductModalTitle}>
            <Typography {...styles.selectedProductErpNo}>{cartLine.erpNumber}</Typography>
            <Typography {...styles.selecteProductTitle}>{cartLine.productName}</Typography>
        </StyledWrapper>
    );

    return (
        <GridContainer {...styles.container} data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                <StyledWrapper>
                    <Link
                        {...styles.productDetailLink}
                        href={cartLine.productUri}
                        alt={`See product details for ${cartLine.erpNumber}`}
                        data-test-selector="cartProductNumberLink"
                    >
                        <Typography {...styles.productErpNumberText}>{cartLine.erpNumber}</Typography>
                    </Link>                
                    {(isNARegion && cartLine.properties?.orgSearchcustomerPartNumber != null) ? (
    <Typography {...styles.customerNumber}>
        {cartLine.properties?.orgSearchcustomerPartNumber}
    </Typography>
) : (
    cartLine.customerName && cartLine.customerName !== "" && (
        <Typography {...styles.customerNumber}>
            {cartLine.customerName}
        </Typography>
    )
)}
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    {cartLine?.properties?.countryOfOrigin && (
                        <Typography {...styles.countryOfOrigin}>
                            {translate("Country of Origin: ")} {cartLine?.properties?.countryOfOrigin}
                        </Typography>
                    )}
                    {cartLine?.properties?.isReturnable !== "true" && (
                        <Typography {...styles.badgeRed}>{translate("Non Returnable")}</Typography>
                    )}
                    {isEmea && cartLine?.pricing?.unitNetPrice === 0 && (
                        <Typography {...styles.badgeRed}>{siteMessage("CartPage_GyorInvalidPartPrice")}</Typography>
                    )}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.cartLineInfoGridItem} className="cartlineInfo">
                <GridContainer {...styles.cartLineInfoContainer}>
                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <GridItem {...styles.productBinNumberGridItem}>
                            <ProductBinNumbers
                                locationId={cart.vmiLocationId}
                                binId={cartLine.vmiBinId}
                                isCondensed={true}
                                extendedStyles={styles.productBinNumbers}
                            />
                        </GridItem>
                    )}
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                                {!cart.cartNotPriced && (
                                    <>
                                        <ProductPrice
                                            showLabel={false}
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={false}
                                            showUnitOfMeasure={true}
                                            showInvalidPriceMessage={true}
                                            extendedStyles={styles.productPrice}
                                        />
                                    </>
                                )}
                                {showInventoryAvailability && !cartLine.quoteRequired && (
                                    <ProductAvailability
                                        productId={cartLine.productId!}
                                        availability={cartLine.availability!}
                                        unitOfMeasure={cartLine.unitOfMeasure ? cartLine.unitOfMeasure : "ea"}
                                        trackInventory={cartLine.trackInventory}
                                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                        extendedStyles={styles.productAvailability}
                                    />
                                )}
                                {errorMessages.length > 0 &&
                                    errorMessages.map((message, index) => (
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                            </GridItem>
                            <GridItem {...styles.quantityGridItem}>
                                {isEmergencyOrder && (
                                    <StyledWrapper>
                                        <Hidden above="md">
                                            <SmallHeadingAndText
                                                heading={translate("Quantity")}
                                                text={cartLine.qtyOrdered?.toString() ?? ""}
                                                extendedStyles={styles.cartExtendedPrice}
                                            />
                                        </Hidden>
                                        <Hidden below="lg">
                                            {cartLine.qtyOrdered?.toString() ?? ""}
                                        </Hidden>
                                        <Button
                                            {...styles.checkAvailabilityButton}
                                            onClick={buttonClickHandler}
                                            variant="tertiary"
                                            data-test-selector="cart_editEmergencyCartLineItemButton"
                                        >
                                            {translate("Edit")}
                                        </Button>
                                        <Modal
                                            headline={selecteProductModalTitle}
                                            isOpen={availabilityModalIsOpen}
                                            handleClose={modalCloseHandler}
                                            {...styles.selectedProductModal}
                                        >
                                            <ProductContext.Provider value={productContext}>
                                                {isLoading ? (<LoadingSpinner></LoadingSpinner>) : <ProductAvailabilityDetails
                                                    customErrorMessage={errorMessage ? errorMessage.toString() : ""}
                                                    productAvilabilityModel={productAvilabilityModel}
                                                    qty={qty}
                                                    quantityDisabled={false}
                                                    orderType={OrderTypes.EMERGENCY}
                                                    setQty={setQuantityHandler}
                                                    setOrderType={setOrderTypeHandler}
                                                    orderTypeDisabled={true}
                                                    updateCartLine={(qtyOrdered: number, location: InventryLocation | undefined) => qtyOrderedChangeHandler(qtyOrdered, location)}
                                                />}
                                            </ProductContext.Provider>

                                        </Modal>
                                    </StyledWrapper>
                                )}

                                {!isEmergencyOrder && (
                                    <CartLineQuantity
                                        showLabel={false}
                                        cart={cart}
                                        editable={true}
                                        onQtyOrderedChange={(qtyOrdered: number) => qtyOrderedChangeHandler(qtyOrdered, undefined)}
                                        extendedStyles={styles.quantity}
                                    />
                                )}
                            </GridItem>
                            <GridItem width={12}>
                                <Hidden above="md">
                                    <SmallHeadingAndText
                                        heading={translate("Extended Price")}
                                        text={cartLine.pricing?.extendedUnitNetPriceDisplay ?? ""}
                                        extendedStyles={styles.cartExtendedPrice}
                                    />

                                </Hidden>
                                <Hidden below="lg">
                                    {translate(cartLine.pricing?.extendedUnitNetPriceDisplay ?? "")}
                                </Hidden>
                            </GridItem>
                            <GridItem width={12}>
                                <Hidden above="md">
                                    <SmallHeadingAndText
                                        heading={translate("Weight")}
                                        text={(cartLine.properties?.weight ?? "0.00") + (isEmea ? " KG" : " LB")}
                                        extendedStyles={styles.cartExtendedPrice}
                                    />
                                </Hidden>
                                <Hidden below="lg">
                                    {translate(cartLine.properties?.weight ?? "0.00") + (isEmea ? " KG" : " LB")}
                                </Hidden>
                            </GridItem>
                            {cart?.properties?.isEmergencyOrder &&
                                cart.properties.isEmergencyOrder.toLocaleLowerCase() === "true" && (
                                    <GridItem width={12} {...styles.shipToLocationGridItem}>
                                        <Hidden above="md">
                                            <SmallHeadingAndText
                                                heading={translate("Ship From Location(s)")}
                                                text={" "}
                                                extendedStyles={styles.cartExtendedPrice}
                                            />
                                        </Hidden>
                                        <StyledWrapper className="distribution-center">
                                            <Typography {...styles.shipToLocationCenter}>
                                                {location ? location?.description : currentWarehouse?.description}
                                            </Typography>
                                            <Typography {...styles.shipToLocationName}>
                                                {translate(
                                                    location ? `${location.city}, ${location.state}` :
                                                        `${currentWarehouse?.city}, ${currentWarehouse?.state}`,
                                                )}
                                            </Typography>
                                            <Link onClick={handleOpenShipDateModal} {...styles.shipToLocationLink}>
                                                {translate("Change Location")}
                                            </Link>
                                            <Modal
                                                isOpen={openShipDateModal}
                                                handleClose={handleCloseShipDateModal}
                                                headline={translate("Estimated Ship Date")}
                                            >
                                                <StyledWrapper {...styles.shipDateModal}>
                                                    <StyledWrapper className="distributionCenter">
                                                        <Typography {...styles.shipToLocationCenter}>
                                                            {location ? location?.description : currentWarehouse?.description}
                                                        </Typography>
                                                        <Typography {...styles.shipToLocationName}>
                                                            {translate(
                                                                location ? `${location.city}, ${location.state}` :
                                                                    `${currentWarehouse?.city}, ${currentWarehouse?.state}`
                                                            )}
                                                        </Typography>
                                                        <Link
                                                            {...styles.shipToLocationLink}
                                                            onClick={handleOpenAvailabilityModal}
                                                        >
                                                            {translate("Edit")}
                                                        </Link>
                                                    </StyledWrapper>
                                                    <StyledWrapper className="shipDate">
                                                        <SmallHeadingAndText
                                                            text={cartLine.properties?.estimatedShipDate}
                                                            heading={translate("Estimated Ship Date")}
                                                            extendedStyles={styles.cartExtendedPrice}
                                                        />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </Modal>
                                            <Modal
                                                headline={selecteProductModalTitle}
                                                isOpen={openAvailabilityModal}
                                                handleClose={handleCloseAvailabilityModal}
                                                {...styles.selectedProductModal}
                                            >

                                                <ProductContext.Provider value={productContext}>
                                                    {isLoading ? (<LoadingSpinner></LoadingSpinner>) : (<ProductAvailabilityDetails
                                                        customErrorMessage={
                                                            errorMessage ? errorMessage.toString() : ""
                                                        }
                                                        productAvilabilityModel={productAvilabilityModel}
                                                        qty={qty}
                                                        quantityDisabled={false}
                                                        orderType={OrderTypes.EMERGENCY}
                                                        setQty={setQuantityHandler}
                                                        setOrderType={setOrderTypeHandler}
                                                        orderTypeDisabled={true}
                                                        updateCartLine={(qtyOrdered: number, location: InventryLocation | undefined) => qtyOrderedChangeHandler(qtyOrdered, location)}
                                                    />)}

                                                </ProductContext.Provider>

                                            </Modal>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            {(!cart?.properties?.isEmergencyOrder ||
                                cart.properties.isEmergencyOrder.toLocaleLowerCase() !== "true") &&
                                (!cart?.properties?.isAnnualReturnsOrder ||
                                    cart.properties.isAnnualReturnsOrder.toLocaleLowerCase() !== "true") && (
                                    <GridItem width={12} {...styles.requestedDatePickerGridItem}>

                                        <DatePicker
                                            {...styles.requestedDatePicker}
                                            format={isEmea ? "dd/MM/yyyy" : "MM/dd/yyyy"}
                                            uid={isEmea ? "requestedShipDateEU" : "requestedShipDate"}
                                            // label={translate("Requested Ship Date")}
                                            onDayChange={requestedShipDateChanged}
                                            selectedDay={requestedShipDateValue ? isEmea ? getInitialShipDate(parseShipDate(requestedShipDateValue)) : parseShipDate(requestedShipDateValue) : new Date()}
                                            dateTimePickerProps={{
                                                value: requestedShipDateValue ? isEmea ? getInitialShipDate(parseShipDate(requestedShipDateValue)) : parseShipDate(requestedShipDateValue) : new Date(),
                                                calendarIcon: null,
                                                clearIcon: null,
                                                minDate: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),
                                                calendarProps: {
                                                    tileDisabled: isEmea ? ({ date, view }: { date: Date; view: string }) => {
                                                        if (view === 'month') {
                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);

                                                            const targetDate = new Date(date);
                                                            targetDate.setHours(0, 0, 0, 0);

                                                            const todayDay = today.getDay(); // 0 = Sun, 5 = Fri, 6 = Sat
                                                            const targetDay = targetDate.getDay();

                                                            if (targetDay === 0 || targetDay === 6) { return true; }

                                                            if (targetDate < today) { return true; }

                                                            if (todayDay === 5 || todayDay === 6 || todayDay === 0) {
                                                                const daysUntilMonday = todayDay === 5 ? 3 : (todayDay === 6 ? 2 : 1);
                                                                const nextMonday = new Date(today);
                                                                nextMonday.setDate(today.getDate() + daysUntilMonday);
                                                                nextMonday.setHours(0, 0, 0, 0);

                                                                if (targetDate < nextMonday) { return true; }
                                                            }

                                                            return false;
                                                        }
                                                        return false;
                                                    } : undefined,
                                                }
                                            }}
                                            labelProps={styles.requestedShipDate}
                                        />

                                    </GridItem>
                                )}
                            {(!cart?.properties?.isAnnualReturnsOrder ||
                                cart.properties.isAnnualReturnsOrder.toLocaleLowerCase() !== "true") && (
                                    <GridItem width={12}>
                                        <StyledWrapper {...styles.estShipDate}>
                                            <Hidden above="md">
                                                <SmallHeadingAndText
                                                    heading={translate("Estimated Ship Date")}
                                                    text={cartLine.properties?.estimatedShipDate}
                                                    extendedStyles={styles.cartExtendedPrice}
                                                />
                                            </Hidden>
                                            <Hidden below="lg">
                                                {translate(cartLine.properties?.estimatedShipDate)}
                                            </Hidden>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.removeCartLineGridItem}>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <Icon src={Trash2} size={20} color={themeColors.primary.richBlack} />
                    </Clickable>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardCondensed));
